import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { FaArrowLeft } from "react-icons/fa";
import Axios from "../../_services/caller.service";
import { useCabineContext } from "../../context/CabineContext";
import styles from "./SavRequestForm.module.scss";

const CONTACT_PREFILL_EXCEPTIONS = [
  {
    displayName: "Nicolas Dezaire",
    email: "nicolas.dezaire@vyv3.fr",
  },
];

const normalizeForCompare = (value) => String(value || "").trim().toLowerCase();

const shouldSkipContactPrefill = (ownerProfile) =>
  CONTACT_PREFILL_EXCEPTIONS.some(
    ({ displayName, email }) =>
      normalizeForCompare(ownerProfile?.display_name) ===
        normalizeForCompare(displayName) &&
      normalizeForCompare(ownerProfile?.user_email) === normalizeForCompare(email)
  );

const normalizeSearchValue = (value) => String(value || "").toLowerCase().trim();

const CabineField = ({ t, cabine, formData, onChange }) => {
  const [cabineSearch, setCabineSearch] = useState("");
  const [isCabineSelectOpen, setIsCabineSelectOpen] = useState(false);
  const searchValue = normalizeSearchValue(cabineSearch);

  const authorizedCabines = useMemo(
    () => (Array.isArray(cabine) ? cabine : []),
    [cabine]
  );

  const displayedCabines = useMemo(() => {
    const filteredCabines = searchValue
      ? authorizedCabines.filter((c) => {
          const cabName = normalizeSearchValue(c.cabname || `Cabine ${c.id}`);
          const cabId = normalizeSearchValue(c.id);

          return cabName.includes(searchValue) || cabId.includes(searchValue);
        })
      : authorizedCabines;

    const selectedCabine = authorizedCabines.find(
      (c) => String(c.id) === String(formData.cabineId)
    );

    if (
      selectedCabine &&
      !filteredCabines.some((c) => String(c.id) === String(selectedCabine.id))
    ) {
      return [selectedCabine, ...filteredCabines];
    }

    return filteredCabines;
  }, [authorizedCabines, formData.cabineId, searchValue]);

  return (
    <label className={styles.field}>
      <span>{t("sav_request.field_cabine")}</span>
      <input
        type="search"
        value={cabineSearch}
        onFocus={() => setIsCabineSelectOpen(true)}
        onChange={(e) => {
          setCabineSearch(e.target.value);
          setIsCabineSelectOpen(true);
        }}
        placeholder={t("sav_request.search_cabine")}
      />
      <select
        name="cabineId"
        value={formData.cabineId}
        onFocus={() => setIsCabineSelectOpen(true)}
        onBlur={() => setIsCabineSelectOpen(false)}
        onChange={(e) => {
          onChange(e);
          setIsCabineSelectOpen(false);
        }}
        size={
          isCabineSelectOpen
            ? Math.min(Math.max(displayedCabines.length + 1, 2), 8)
            : undefined
        }
        required
      >
        <option value="">{t("sav_request.select_cabine")}</option>
        {displayedCabines.length > 0 ? (
          displayedCabines.map((c) => (
            <option key={c.id} value={c.id}>
              {c.cabname || `Cabine ${c.id}`}
            </option>
          ))
        ) : (
          <option value="" disabled>
            {t("sav_request.no_cabine_found")}
          </option>
        )}
      </select>
    </label>
  );
};

const SavRequestForm = ({ ownerProfile, ownerId, onClose }) => {
  const { t } = useTranslation();
  const { cabine } = useCabineContext();
  const skipContactPrefill = shouldSkipContactPrefill(ownerProfile);
  const [formData, setFormData] = useState({
    name: skipContactPrefill ? "" : ownerProfile?.display_name || "",
    email: skipContactPrefill ? "" : ownerProfile?.user_email || "",
    phone: ownerProfile?.phone || "",
    cabineId: "",
    centerName: "",
    address: "",
    address2: "",
    zipCode: "",
    city: "",
    country: "",
    materialType: "",
    materialModel: "",
    serialNumber: "",
    issue: "",
    message: "",
  });
  const [addressLoading, setAddressLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [successRef, setSuccessRef] = useState("");

  const selectedCabine = cabine?.find(
    (c) => String(c.id) === String(formData.cabineId)
  );

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const fetchCabineAddress = useCallback(async (cabId) => {
    if (!cabId) return;
    setAddressLoading(true);
    try {
      const res = await Axios.get(`/calibrationRequest/address/${cabId}`);
      if (!res.data.error && res.data.data) {
        const { address, address2, zipCode, city, country, phone } = res.data.data;
        setFormData((prev) => ({
          ...prev,
          address: address || "",
          address2: address2 || "",
          zipCode: zipCode || "",
          city: city || "",
          country: country || "",
          ...(phone ? { phone } : {}),
        }));
      }
    } catch (err) {
      // Pas bloquant, l'utilisateur peut remplir manuellement.
    } finally {
      setAddressLoading(false);
    }
  }, []);

  useEffect(() => {
    if (formData.cabineId) {
      fetchCabineAddress(formData.cabineId);
    }
  }, [formData.cabineId, fetchCabineAddress]);

  useEffect(() => {
    setFormData((prev) => ({
      ...prev,
      name: (prev.name || skipContactPrefill)
        ? prev.name
        : ownerProfile?.display_name || "",
      email: (prev.email || skipContactPrefill)
        ? prev.email
        : ownerProfile?.user_email || "",
      phone: prev.phone || ownerProfile?.phone || "",
    }));
  }, [
    ownerProfile?.display_name,
    ownerProfile?.phone,
    ownerProfile?.user_email,
    skipContactPrefill,
  ]);

  useEffect(() => {
    if (selectedCabine) {
      setFormData((prev) => ({
        ...prev,
        centerName: selectedCabine.cabname || "",
      }));
    }
  }, [selectedCabine]);

  const buildPayload = () => ({
    name: formData.name,
    email: formData.email,
    phone: formData.phone,
    cabineName:
      formData.centerName ||
      selectedCabine?.cabname ||
      `Cabine ${formData.cabineId}`,
    address: [formData.address, formData.address2].filter(Boolean).join(", "),
    zipCode: formData.zipCode,
    city: formData.city,
    country: formData.country,
    materialType: formData.materialType,
    materialModel: formData.materialModel,
    serialNumber: formData.serialNumber,
    issue: formData.issue,
    message: formData.message,
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const res = await Axios.post(`/savRequest/${ownerId}`, buildPayload());
      setSuccessRef(res.data?.refNumber || "");
    } catch (err) {
      setError(t("sav_request.error"));
    } finally {
      setLoading(false);
    }
  };

  if (successRef) {
    return (
      <div className={styles.successMsg}>
        <p>{t("sav_request.success", { ref: successRef })}</p>
        <button className={styles.doneBtn} onClick={onClose} type="button">
          {t("sav_request.close")}
        </button>
      </div>
    );
  }

  return (
    <div className={styles.pageContainer}>
      <button className={styles.backToMenu} onClick={onClose} type="button">
        <FaArrowLeft /> {t("sav_request.back")}
      </button>

      <div className={styles.formLayout}>
        <div className={styles.headerInfo}>
          <h4>{t("sav_request.form_title")}</h4>
          <p>{t("sav_request.form_desc")}</p>
        </div>

        <form className={styles.form} onSubmit={handleSubmit}>
          <CabineField
            t={t}
            cabine={cabine}
            formData={formData}
            onChange={handleChange}
          />

          <label className={styles.field}>
            <span>{t("sav_request.field_name")}</span>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
            />
          </label>

          <label className={styles.field}>
            <span>{t("sav_request.field_email")}</span>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              required
            />
          </label>

          <label className={styles.field}>
            <span>{t("sav_request.field_phone")}</span>
            <input
              type="tel"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              placeholder={t("sav_request.phone_placeholder")}
            />
          </label>

          <fieldset className={styles.addressGroup}>
            <legend>{t("sav_request.field_address_title")}</legend>
            {addressLoading && (
              <p className={styles.addressLoading}>
                {t("sav_request.loading_address")}
              </p>
            )}
            <label className={styles.field}>
              <span>{t("sav_request.field_center_name")}</span>
              <input
                type="text"
                name="centerName"
                value={formData.centerName}
                onChange={handleChange}
                required
              />
            </label>
            <label className={styles.field}>
              <span>{t("sav_request.field_address")}</span>
              <input
                type="text"
                name="address"
                value={formData.address}
                onChange={handleChange}
              />
            </label>
            <label className={styles.field}>
              <span>{t("sav_request.field_address2")}</span>
              <input
                type="text"
                name="address2"
                value={formData.address2}
                onChange={handleChange}
              />
            </label>
            <div className={styles.addressRow}>
              <label className={styles.field}>
                <span>{t("sav_request.field_zipcode")}</span>
                <input
                  type="text"
                  name="zipCode"
                  value={formData.zipCode}
                  onChange={handleChange}
                />
              </label>
              <label className={styles.field}>
                <span>{t("sav_request.field_city")}</span>
                <input
                  type="text"
                  name="city"
                  value={formData.city}
                  onChange={handleChange}
                />
              </label>
            </div>
            <label className={styles.field}>
              <span>{t("sav_request.field_country")}</span>
              <input
                type="text"
                name="country"
                value={formData.country}
                onChange={handleChange}
              />
            </label>
          </fieldset>

          <label className={styles.field}>
            <span>{t("sav_request.field_material_type")}</span>
            <select
              name="materialType"
              value={formData.materialType}
              onChange={handleChange}
              required
            >
              <option value="">{t("sav_request.select_material_type")}</option>
              <option value="Casque aerien">{t("sav_request.material_headphones")}</option>
              <option value="Inserts">{t("sav_request.material_inserts")}</option>
              <option value="Conduction osseuse">{t("sav_request.material_bone")}</option>
              <option value="Microphone">{t("sav_request.material_microphone")}</option>
              <option value="Carte son">{t("sav_request.material_soundcard")}</option>
              <option value="Autre">{t("sav_request.material_other")}</option>
            </select>
          </label>

          <label className={styles.field}>
            <span>{t("sav_request.field_material_model")}</span>
            <input
              type="text"
              name="materialModel"
              value={formData.materialModel}
              onChange={handleChange}
              required
            />
          </label>

          <label className={styles.field}>
            <span>{t("sav_request.field_serial_number")}</span>
            <input
              type="text"
              name="serialNumber"
              value={formData.serialNumber}
              onChange={handleChange}
            />
          </label>

          <label className={styles.field}>
            <span>{t("sav_request.field_issue")}</span>
            <textarea
              name="issue"
              value={formData.issue}
              onChange={handleChange}
              rows={4}
              required
            />
          </label>

          <label className={styles.field}>
            <span>{t("sav_request.field_message")}</span>
            <textarea
              name="message"
              value={formData.message}
              onChange={handleChange}
              rows={3}
              placeholder={t("sav_request.message_placeholder")}
            />
          </label>

          {error && <p className={styles.error}>{error}</p>}

          <button className={styles.submitBtn} type="submit" disabled={loading}>
            {loading ? t("sav_request.sending") : t("sav_request.submit")}
          </button>
        </form>
      </div>
    </div>
  );
};

export default SavRequestForm;
